// src/shared/hooks/useDebounce.ts
//
// Generic value-debounce hook. Delays propagating a rapidly-changing
// value (e.g. a search input on every keystroke) until the caller has
// paused for `delay` ms. Used by <GlobalSearchBar /> to throttle the
// keystroke -> API-call rate so the ultra-fast /global-search endpoint
// is hit once per pause instead of once per character.
//
// Lives under shared/hooks (not a feature folder) because it is a
// domain-agnostic primitive any future feature can reuse.

import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delay`
 * milliseconds have elapsed since the last change to `value`.
 *
 * @param value The fast-changing source value.
 * @param delay Debounce window in milliseconds.
 */
export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        // Schedule the update `delay` ms from now.
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        // If `value` (or `delay`) changes before the timer fires, cancel
        // the pending update and start a fresh window. This is what makes
        // the hook debounce rather than merely delay.
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export default useDebounce;
